
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMFA } from "@/contexts/MFAContext";
import { useProvider } from "@/contexts/ProviderContext";
import LandingPage from "./LandingPage";
import DashboardPage from "./DashboardPage";
import { toast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton"; // For loading state
import { logger } from '@/lib/logger';

const RootPage = () => {
  const { user, isLoading, subscription, isSubscriptionLoading, fetchSubscriptionStatus } = useAuth();
  const { hasEnrolledTOTP, needsMFAVerification, isLoadingMFAStatus } = useMFA();
  const { connectionStatus, isCheckingConnections } = useProvider();
  const navigate = useNavigate();

  // Stripe checkout returns now land on /post-checkout (see create-checkout
  // success_url). CHECKOUT_SUCCESS + PAID_ACTIVATION Umami events moved to
  // /trial-started and /purchase-success. We only handle checkout_canceled
  // here since cancel_url still points at the root.
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("checkout_canceled") === "true") {
      toast({
        variant: "default",
        title: "Checkout Canceled",
        description: "Your subscription was not completed. You can try again from your plan page.",
      });
      navigate("/select-tier", { replace: true });
    }
  }, [fetchSubscriptionStatus, navigate]);

  // MFA enforcement for authenticated users (must come before tier selection)
  useEffect(() => {
    // Only check MFA for authenticated users after MFA status is loaded
    if (!user || isLoadingMFAStatus) {
      return;
    }

    logger.debug("🔐 RootPage: Checking MFA status...");
    logger.debug("🔐 RootPage: hasEnrolledTOTP?", hasEnrolledTOTP);
    logger.debug("🔐 RootPage: needsMFAVerification?", needsMFAVerification);

    // If user hasn't enrolled MFA, force enrollment
    if (!hasEnrolledTOTP) {
      logger.debug("🚀 RootPage: REDIRECTING TO /mfa/setup (no MFA enrolled)");
      navigate("/mfa/setup", { replace: true });
      return;
    }

    // If user has MFA but session needs verification (aal1 -> aal2)
    if (needsMFAVerification) {
      logger.debug("🚀 RootPage: REDIRECTING TO /mfa/verify (MFA verification needed)");
      navigate("/mfa/verify", { state: { from: "/" }, replace: true });
      return;
    }

    logger.debug("✅ RootPage: MFA check passed");
  }, [user, hasEnrolledTOTP, needsMFAVerification, isLoadingMFAStatus, navigate]);

  // Redirect authenticated users without Jotform connection
  // This runs AFTER MFA check - user must have MFA verified before connecting Jotform
  useEffect(() => {
    // Don't check until MFA is resolved and connections are checked
    if (!user || isLoadingMFAStatus || !hasEnrolledTOTP || needsMFAVerification || isCheckingConnections) {
      return;
    }

    logger.debug("🔗 RootPage: Checking Jotform connection...", { jotform: connectionStatus.jotform });

    if (connectionStatus.jotform === false) {
      logger.debug("🚀 RootPage: REDIRECTING TO /connect-jotform");
      navigate("/connect-jotform", { replace: true });
      return;
    }

    logger.debug("✅ RootPage: Jotform connection check passed");
  }, [user, connectionStatus.jotform, isCheckingConnections, hasEnrolledTOTP, needsMFAVerification, isLoadingMFAStatus, navigate]);

  // Redirect authenticated users without a tier to tier selection
  // This runs AFTER MFA and Jotform connection checks
  useEffect(() => {
    // Don't check tier until MFA is resolved and Jotform is connected
    if (!user || isLoadingMFAStatus || !hasEnrolledTOTP || needsMFAVerification) {
      return;
    }
    if (isCheckingConnections || connectionStatus.jotform !== true) {
      return;
    }

    logger.debug("🔍 RootPage: Checking tier redirect...");
    logger.debug("🔍 RootPage: subscription_tier?", subscription?.subscription_tier);

    if (user && !isSubscriptionLoading && !subscription?.subscription_tier) {
      logger.debug("🚀 RootPage: REDIRECTING TO /select-tier");
      navigate("/select-tier", { replace: true });
    } else {
      logger.debug("❌ RootPage: NOT redirecting");
    }
  }, [user, subscription, isSubscriptionLoading, hasEnrolledTOTP, needsMFAVerification, isLoadingMFAStatus, connectionStatus.jotform, isCheckingConnections, navigate]);

  // Show loading while auth or subscription is loading
  // For authenticated users, also wait for MFA status
  const isAuthenticatedAndLoading = user && (isLoadingMFAStatus || isCheckingConnections || isSubscriptionLoading);

  if (isLoading || isAuthenticatedAndLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // For authenticated users, only show dashboard if MFA is complete
  // The useEffect above handles redirects, but we also guard here to prevent flash
  if (user) {
    // If MFA isn't set up or needs verification, don't render dashboard
    // (the useEffect will redirect, this just prevents flash of content)
    if (!hasEnrolledTOTP || needsMFAVerification) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="w-full max-w-md space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      );
    }
    return <DashboardPage />;
  }
  return <LandingPage />;
};

export default RootPage;
